// awp_hero-video.js
// Lazy-loads hero background videos so they never block first paint, and streams
// them adaptively (HLS) so only the watched segments download.
//
// The hero section renders a lightweight <img> poster (the LCP element) on top
// of a <video preload="none"> that has NO `autoplay` attribute. Nothing about
// the video downloads until this controller starts it, which happens only when:
//   1. the video scrolls near the viewport (IntersectionObserver), AND
//   2. the video is actually laid out for the current breakpoint.
//
// Because the off-breakpoint <video> lives inside a `display:none` wrapper, it
// has no layout box and IntersectionObserver never reports it as intersecting —
// so ONLY the source matching the current breakpoint is ever fetched. The extra
// matchMedia guard is belt-and-suspenders for that same rule.
//
// Playback source, in order of preference:
//   1. HLS (data-hls-src): Safari plays it natively; other browsers load the
//      self-hosted hls.js (data-hls-loader) and stream via MSE. Adaptive quality,
//      minimal data — the full-length video plays without downloading it whole.
//   2. Progressive MP4: the <source> the video_tag emits (used when there's no
//      m3u8 or HLS can't run). Still range-streamed, just not adaptive.
//
// Once the matching video is playing, the poster fades out over it.
(function () {
  if (window.__awpHeroVideoInit) return;
  window.__awpHeroVideoInit = true;

  var hlsLoaderPromise = null;

  // Load the self-hosted hls.js once; resolve with the global Hls constructor.
  function loadHls(src) {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (hlsLoaderPromise) return hlsLoaderPromise;
    hlsLoaderPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () {
        window.Hls ? resolve(window.Hls) : reject(new Error('hls.js loaded but Hls global missing'));
      };
      s.onerror = function () {
        reject(new Error('hls.js failed to load'));
      };
      document.head.appendChild(s);
    });
    return hlsLoaderPromise;
  }

  function play(video) {
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function hidePosterWhenPlaying(video) {
    video.addEventListener(
      'playing',
      function () {
        var wrapper = video.parentElement;
        var poster = wrapper && wrapper.querySelector('.hero__video-poster');
        if (poster) poster.classList.add('hero__video-poster--hidden');
      },
      { once: true }
    );
  }

  // Progressive MP4 fallback: play the <source> the markup already contains.
  function startProgressive(video) {
    video.preload = 'auto';
    if (video.readyState >= 2) {
      play(video);
    } else {
      video.addEventListener('loadeddata', function () { play(video); }, { once: true });
      video.load(); // preload="none" won't fetch on its own
    }
  }

  function start(video) {
    hidePosterWhenPlaying(video);

    var hlsSrc = video.dataset.hlsSrc;

    // No HLS playlist available → progressive MP4.
    if (!hlsSrc) {
      startProgressive(video);
      return;
    }

    // Safari (and iOS) play HLS natively — point the element straight at the
    // playlist, which supersedes the <source> children.
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.preload = 'auto';
      video.src = hlsSrc;
      if (video.readyState >= 2) {
        play(video);
      } else {
        video.addEventListener('loadeddata', function () { play(video); }, { once: true });
        video.load();
      }
      return;
    }

    // Everyone else: stream the playlist through hls.js (MSE).
    loadHls(video.dataset.hlsLoader)
      .then(function (Hls) {
        if (!Hls.isSupported()) {
          startProgressive(video);
          return;
        }
        var hls = new Hls({
          capLevelToPlayerSize: true, // pick a rendition matching the rendered size
          maxBufferLength: 15, // don't pre-buffer the whole loop
          maxMaxBufferLength: 30,
        });
        hls.loadSource(hlsSrc);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, function () {
          play(video);
        });
        hls.on(Hls.Events.ERROR, function (_evt, data) {
          if (data && data.fatal) {
            hls.destroy();
            startProgressive(video); // fall back rather than show nothing
          }
        });
      })
      .catch(function () {
        startProgressive(video); // hls.js unavailable → progressive MP4
      });
  }

  function init() {
    var videos = document.querySelectorAll('video[data-hero-video]');
    if (!videos.length) return;

    var desktop = window.matchMedia('(min-width: 750px)');

    if (!('IntersectionObserver' in window)) {
      // No IO support: just start whatever matches the breakpoint.
      Array.prototype.forEach.call(videos, function (video) {
        var forDesktop = video.dataset.heroVideo === 'desktop';
        if (forDesktop === desktop.matches) start(video);
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var video = entry.target;
          var forDesktop = video.dataset.heroVideo === 'desktop';
          if (forDesktop !== desktop.matches) return;
          observer.unobserve(video);
          start(video);
        });
      },
      { rootMargin: '200px' }
    );

    Array.prototype.forEach.call(videos, function (video) {
      io.observe(video);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
